import type { PaymentsDto } from '@customTypes/types';
import { PaymentFrequency } from '@customTypes/types';
import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import { paymentsFormSchema } from '@/pages/club/onboard/components/stages/payments/constants/schema';
import {
  useClubProfilePaymentsMutation,
  useClubProfilePaymentsQuery
} from '@/shared/api/requests/club';
import { useCurrencyStore } from '@/shared/store/store';

export const usePaymentsPage = () => {
  const { formatMessage } = useIntl();
  const { id: clubId } = useParams({ from: '/clubOwner/$id' });
  const paymentsQuery = useClubProfilePaymentsQuery({ clubId });
  const queryClient = useQueryClient();
  const paymentsMutation = useClubProfilePaymentsMutation();
  const navigate = useNavigate({ from: '/clubOwner/$id/onboard/$step' });
  const profileCurrency = useCurrencyStore((state) => state.profileCurrency);

  const queries = [paymentsQuery];
  const isLoading = queries.some((q) => q.isLoading);

  const form = useForm<{
    data: PaymentsDto;
    additional: {
      clubFee: boolean;
      forShift: 'Common' | 'Separated';
      forShiftCommon: 'Bid' | 'Guarantee';
      deposit: boolean;
      show: boolean;
      tips: boolean;
      crazyMenu: boolean;
      consummationItems: boolean;
    };
  }>({
    validateInputOnBlur: true,
    initialValues: {
      data: {
        clubFee: 0,
        clubFreeFrequency: PaymentFrequency.Monthly,
        dailyRateMin: 0,
        dailyRateMax: 0,
        dailyRatePaymentFrequency: PaymentFrequency.Monthly,
        guarantee: 0,
        guaranteePaymentFrequency: PaymentFrequency.Monthly,
        full: 0,
        fullPaymentFrequency: PaymentFrequency.Monthly,
        topless: 0,
        toplessPaymentFrequency: PaymentFrequency.Monthly,
        goGo: 0,
        goGoPaymentFrequency: PaymentFrequency.Monthly,
        show: 0,
        showPaymentFrequency: PaymentFrequency.Monthly,
        tips: 0,
        tipsPaymentFrequency: PaymentFrequency.Monthly,
        crazyMenu: [],
        consummationItems: [],
        deposit: 0,
        crazyMenuPercent: 0,
        crazyMenuPaymentFrequency: PaymentFrequency.Monthly,
        consummationFrequency: PaymentFrequency.Monthly
      },
      additional: {
        clubFee: false,
        forShift: 'Common',
        forShiftCommon: 'Bid',
        deposit: false,
        show: false,
        tips: false,
        crazyMenu: false,
        consummationItems: false
      }
    },
    validate: zodResolver(paymentsFormSchema),
    transformValues: (values) => ({
      data: {
        ...values.data,
        clubFee: values.additional.clubFee ? values.data.clubFee : 0,
        dailyRateMin:
          values.additional.forShiftCommon === 'Bid' &&
          values.additional.forShift === 'Common' &&
          !values.additional.clubFee
            ? values.data.dailyRateMin
            : 0,
        guarantee:
          values.additional.forShift === 'Common' &&
          values.additional.forShiftCommon === 'Guarantee' &&
          !values.additional.clubFee
            ? values.data.guarantee
            : 0,
        full:
          values.additional.forShift === 'Separated' &&
          !values.additional.clubFee
            ? values.data.full
            : 0,
        deposit:
          values.additional.deposit && !values.additional.clubFee
            ? values.data.deposit
            : 0,
        topless:
          values.additional.forShift === 'Separated' &&
          !values.additional.clubFee
            ? values.data.topless
            : 0,
        goGo:
          values.additional.forShift === 'Separated' &&
          !values.additional.clubFee
            ? values.data.goGo
            : 0,
        show: values.additional.show ? values.data.show : 0,
        tips: values.additional.tips ? values.data.tips : 0,
        crazyMenu: values.additional.crazyMenu ? values.data.crazyMenu : [],
        consummationItems: values.additional.consummationItems
          ? values.data.consummationItems
          : [],
        consummationFrequency: values.additional.consummationItems
          ? values.data.consummationFrequency
          : PaymentFrequency.Monthly,
        crazyMenuPercent: values.additional.crazyMenu
          ? values.data.crazyMenuPercent
          : 0,
        crazyMenuPaymentFrequency: values.additional.crazyMenu
          ? values.data.crazyMenuPaymentFrequency
          : PaymentFrequency.Monthly
      },
      additional: {
        ...values.additional
      }
    })
  });

  const next = () => {
    navigate({
      to: '/clubOwner/$id/onboard/$step',
      params: {
        step: 'work-conditions'
      }
    });
  };

  useEffect(() => {
    if (paymentsQuery.isSuccess && paymentsQuery.data?.data) {
      form.initialize({
        data: { ...paymentsQuery.data?.data },
        additional: {
          clubFee: paymentsQuery.data?.data.clubFee !== 0,
          forShift:
            (paymentsQuery.data.data.dailyRateMin !== 0 ||
              paymentsQuery.data.data.guarantee !== 0) &&
            !paymentsQuery.data.data.clubFee
              ? 'Common'
              : 'Separated',
          forShiftCommon:
            paymentsQuery.data.data.dailyRateMin !== 0 &&
            !paymentsQuery.data.data.clubFee
              ? 'Bid'
              : 'Guarantee',
          deposit: paymentsQuery.data.data.deposit !== 0,
          show: paymentsQuery.data.data.show !== 0,
          tips: paymentsQuery.data.data.tips !== 0,
          crazyMenu: !!paymentsQuery.data.data.crazyMenu.length,
          consummationItems: !!paymentsQuery.data.data.consummationItems.length
        }
      });
    }
  }, [paymentsQuery.data?.data]);

  const onSubmit = form.onSubmit(async (values) => {
    await paymentsMutation.mutateAsync(
      {
        params: {
          clubId,
          data: values.data
        }
      },
      {
        onSuccess: async () => {
          notifications.show({
            title: formatMessage({ id: 'notifications.successDataUpdate' }),
            ...notificationStyleConfig
          });
          await queryClient.invalidateQueries({
            queryKey: ['ClubProfilePayments', clubId]
          });
          next();
        }
      }
    );
  });

  return {
    state: { isLoading, form, profileCurrency },
    functions: { onSubmit }
  };
};
